import { Check, Eye, X, Ban, CheckCircle2 } from "lucide-react";
import AdminPagination from "../../components/admin/AdminPagination";
import { formatTripRange } from "../../lib/dateLocal";
import { formatUsd } from "../../lib/currencyDisplay";
import { formatBookingStatus, bookingStatusDotClass } from "../../lib/bookingStatus";

export default function AdminPartnerTab({
  partnerBookings,
  page,
  totalPages,
  onPageChange,
  onConfirm,
  onReject,
  onCancelRefund,
  onViewDetails,
  onMarkFulfilled,
}) {
  return (
    <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table mt-4" data-testid="admin-partner">
      <div className="px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] text-xs text-[var(--zc-text-2)]">
        Recent partner inventory reservations — confirmed, pending, and cancelled. Use Confirm for legacy pending rows; Cancel &amp; refund runs the admin cancellation flow with a full refund.
      </div>
      {partnerBookings.length === 0 && (
        <div className="px-4 sm:px-6 py-10 text-center text-[var(--zc-text-3)] text-sm" data-testid="admin-partner-empty">
          No partner reservations found.
        </div>
      )}
      <div className="hidden lg:grid lg:grid-cols-[1.2fr,0.9fr,0.9fr,0.6fr,0.7fr,0.6fr,0.7fr,0.7fr,auto] zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
        <div>Vehicle</div>
        <div>Renter</div>
        <div>Dates</div>
        <div>Total</div>
        <div>Status</div>
        <div>Payment</div>
        <div>Partner</div>
        <div>Conf #</div>
        <div>Actions</div>
      </div>
      {partnerBookings.map((b) => {
        const snap = b.vehicle_snapshot || {};
        const isPending = b.status === "pending_partner_confirmation";
        const isConfirmed = b.status === "confirmed";
        const isCancelled = b.status === "cancelled" || b.status === "expired";
        const canCancelRefund = isConfirmed && !b.partner_fulfilled_at;
        const canFulfill = isConfirmed && !b.partner_fulfilled_at;
        return (
          <div
            key={b.booking_id}
            className="zc-stack-row lg:grid lg:grid-cols-[1.2fr,0.9fr,0.9fr,0.6fr,0.7fr,0.6fr,0.7fr,0.7fr,auto] lg:items-center gap-3 px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0 text-sm"
            data-testid={`admin-partner-row-${b.booking_id}`}
          >
            <div data-label="Vehicle" className="min-w-0">
              <div className="font-semibold">
                {snap.make ? `${snap.year || ""} ${snap.make} ${snap.model}`.trim() : b.vehicle_id || "—"}
              </div>
              <div className="text-2xs font-mono text-[var(--zc-text-3)] mt-0.5 break-all">{b.booking_id}</div>
              {b.partner_fulfilled_at && (
                <div className="text-2xs text-[var(--zc-accent)] mt-0.5" data-testid={`partner-fulfilled-${b.booking_id}`}>
                  Fulfilled
                </div>
              )}
            </div>
            <div data-label="Renter" className="text-[var(--zc-text-2)] min-w-0">
              <div>{b.renter_name || "—"}</div>
              {b.contact_phone && <div className="text-2xs text-[var(--zc-text-3)] mt-0.5">{b.contact_phone}</div>}
            </div>
            <div data-label="Dates" className="text-[var(--zc-text-2)] text-xs break-words">
              {formatTripRange(b.start_date, b.end_date) || "—"}
            </div>
            <div data-label="Total" className="font-semibold">${formatUsd(b.total)}</div>
            <div data-label="Status" className="flex items-center gap-2">
              <span className={"dot " + bookingStatusDotClass(b.status)} />
              {formatBookingStatus(b)}
            </div>
            <div data-label="Payment" className="text-[var(--zc-text-2)] text-xs capitalize">
              {b.payment_method?.replace(/_/g, " ") || "—"}
            </div>
            <div data-label="Partner" className="text-[var(--zc-text-2)] text-xs min-w-0 break-words">
              {b.external_partner || "—"}
            </div>
            <div data-label="Conf #" className="text-2xs font-mono text-[var(--zc-text-3)] break-all">
              {b.partner_confirmation_number || "—"}
            </div>
            <div data-label="Actions" className="zc-stack-actions flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onViewDetails(b)}
                className="px-3 h-9 zc-btn-ghost text-xs flex items-center gap-1"
                data-testid={`partner-view-${b.booking_id}`}
              >
                <Eye size={12} /> Details
              </button>
              {isPending && (
                <>
                  <button
                    type="button"
                    onClick={() => onConfirm(b.booking_id)}
                    className="px-3 h-9 zc-btn-primary text-xs flex items-center gap-1"
                    data-testid={`partner-confirm-${b.booking_id}`}
                  >
                    <Check size={12} /> Confirm
                  </button>
                  <button
                    type="button"
                    onClick={() => onReject(b.booking_id)}
                    className="px-3 h-9 zc-btn-ghost text-xs flex items-center gap-1"
                    data-testid={`partner-reject-${b.booking_id}`}
                  >
                    <X size={12} /> Reject
                  </button>
                </>
              )}
              {canFulfill && (
                <button
                  type="button"
                  onClick={() => onMarkFulfilled(b.booking_id)}
                  className="px-3 h-9 zc-btn-ghost text-xs flex items-center gap-1"
                  data-testid={`partner-fulfill-${b.booking_id}`}
                >
                  <CheckCircle2 size={12} /> Mark fulfilled
                </button>
              )}
              {canCancelRefund && (
                <button
                  type="button"
                  onClick={() => onCancelRefund(b)}
                  className="px-3 h-9 zc-btn-ghost text-xs flex items-center gap-1 text-[var(--zc-danger)]"
                  data-testid={`partner-cancel-refund-${b.booking_id}`}
                >
                  <Ban size={12} /> Cancel &amp; refund
                </button>
              )}
              {isCancelled && (
                <span className="text-2xs text-[var(--zc-text-3)] self-center">No actions</span>
              )}
            </div>
          </div>
        );
      })}
      <AdminPagination page={page} totalPages={totalPages} onPageChange={onPageChange} testid="admin-partner-pagination" />
    </div>
  );
}
